import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Stack, SxProps } from '@mui/material';
import {
  PointOfInterest,
  ImageType,
  FileUploadStatus,
  useUploadAttachmentsToPoiMutation,
  SelectedPointOfInterestDocument,
} from '@/__generated__/graphql';
import { usePoiPostInspectionNoteMutation } from '@/hooks/usePoiPostInspectionNoteMutation';
import { useHavePermission } from '@/hooks';
import { usePoiAttachments } from '@/hooks/usePoiAttachments';
import {
  poiAttachmentsAtom,
  postInspectionImagesAtom,
} from '@/components/Analysis/modules/pointOfInterest/state/atoms';
import { Permissions } from '@/types';
import { LocationTag, MAX_FILE_UPLOAD_LIMIT } from '@/constants';
import { Attachment } from '../../types';
import { AttachmentsViewer } from '../AttachmentsViewer';
import validateFiles from '../ValidateFiles';
import { PostInspectionNotesEditor } from './PostInspectionNotesEditor';
import { FileDisplay } from '@/components/Analysis/shared/MediaUploadsAndViewer/FileDisplay';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { MediaWrapperContainer, MediaSectionTitle, UploadButton, UploadButtonText } from './styles';

type Props = {
  selectedPoiInfo: PointOfInterest;
  isUpdating?: boolean;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode?: boolean;
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  sx?: SxProps;
};

export const PostInspectionMedia = ({
  selectedPoiInfo,
  isUpdating: externalIsUpdating,
  setIsUpdating: externalSetIsUpdating,
  isEditMode = false,
  saveRef,
  sx,
}: Props) => {
  const { updateNote } = usePoiPostInspectionNoteMutation();
  const userCanUploadMedia = useHavePermission(Permissions.CREATE_UPLOAD_MEDIA);
  const [internalIsUpdating, setInternalIsUpdating] = useState(false);
  const isUpdating = externalIsUpdating ?? internalIsUpdating;
  const setIsUpdating = externalSetIsUpdating ?? setInternalIsUpdating;
  const [uploadAttachmentsToPoi] = useUploadAttachmentsToPoiMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  usePoiAttachments({
    selectedPoiInfo,
    isUpdating,
  });

  const postInspectionImages = useAtomValue(postInspectionImagesAtom);
  const setPoiAttachments = useSetAtom(poiAttachmentsAtom);

  const initialNote = selectedPoiInfo.postInspectionNote ?? '';
  const [pendingNote, setPendingNote] = useState<string | null | undefined>(null);
  const [isNoteEditing, setIsNoteEditing] = useState(false);
  const [pendingNewAttachments, setPendingNewAttachments] = useState<Attachment[]>([]);

  const postInspectionImagesUpload = useCallback(
    async (filesToUpload: Attachment[]) => {
      setIsUpdating(true);
      setPoiAttachments((prev) => [...prev, ...filesToUpload]);
      try {
        const { data } = await uploadAttachmentsToPoi({
          variables: {
            input: {
              pointOfInterestId: selectedPoiInfo.id,
              files: filesToUpload.map(({ uuid, name, type, size }) => ({
                uuid: uuid || '',
                name,
                size: size || 0,
                mimeType: type,
                imageType: ImageType.PostInspection,
              })),
              lastModifiedFor: 'Media Upload',
            },
          },
          onError: (error) => {
            showSnackBar(error.message, 'error');
            setPoiAttachments((prev) =>
              prev.filter((a) => !filesToUpload.some((f) => f.uuid === a.uuid))
            );
            setIsUpdating(false);
          },
        });

        if (data?.uploadAttachmentsToPoi?.length) {
          setPoiAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = data.uploadAttachmentsToPoi?.find((u) => u.uuid === attachment.uuid);
              if (uploaded) {
                return {
                  ...attachment,
                  url: uploaded.url,
                  resourcePath: uploaded.resourcePath,
                  id: uploaded.id,
                  uploadStatus: FileUploadStatus.Success,
                };
              }
              return attachment;
            })
          );
        }
        showSnackBar('Files uploaded successfully', 'success');
      } catch (error) {
        console.error('Error uploading files:', error);
        showSnackBar('Failed to upload files', 'error');
        setPoiAttachments((prev) =>
          prev.filter((a) => !filesToUpload.some((f) => f.uuid === a.uuid))
        );
        setIsUpdating(false);
      }
    },
    [selectedPoiInfo.id, uploadAttachmentsToPoi, showSnackBar, setIsUpdating, setPoiAttachments]
  );

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      const totalFiles =
        postInspectionImages.length + pendingNewAttachments.length + (files?.length || 0);
      if (totalFiles > MAX_FILE_UPLOAD_LIMIT) {
        showSnackBar(
          `You can only upload a maximum of ${MAX_FILE_UPLOAD_LIMIT} files in total.`,
          'error'
        );
        return;
      }
      if (files && files.length > 0) {
        const fileArray = [...files];
        const { validFiles, fileErrors } = validateFiles(fileArray);
        if (fileErrors.length > 0) {
          fileErrors.forEach((error) => showSnackBar(error, 'error'));
          return;
        }

        const allExisting = [...postInspectionImages, ...pendingNewAttachments];
        const duplicateFiles = validFiles.filter((file) =>
          allExisting.some((att) => att.name === file.name.replace(/[^\w.-]/g, '_'))
        );
        if (duplicateFiles.length > 0) {
          showSnackBar(
            `Duplicate file names found: ${duplicateFiles.map((f) => f.name).join(', ')}`,
            'error'
          );
          return;
        }

        const newAttachments: Attachment[] = validFiles.map((file, index) => ({
          uuid: `file-${Date.now()}-${index}`,
          name: file.name,
          type: file.type,
          isNew: true,
          size: file.size,
          file,
          uploadStatus: FileUploadStatus.Inprogress,
          imageType: ImageType.PostInspection,
        }));
        if (isEditMode) {
          setPendingNewAttachments((prev) => [...prev, ...newAttachments]);
        } else {
          await postInspectionImagesUpload(newAttachments);
        }
      }
      event.target.value = '';
    },
    [
      postInspectionImages,
      pendingNewAttachments,
      showSnackBar,
      isEditMode,
      postInspectionImagesUpload,
    ]
  );

  const handleRemovePendingAttachment = useCallback((index: number) => {
    setPendingNewAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    setIsUpdating(true);
    try {
      if (pendingNote !== initialNote && pendingNewAttachments.length === 0) {
        await updateNote(pendingNote, selectedPoiInfo.id);
      }

      if (pendingNewAttachments.length > 0) {
        setPoiAttachments((prev) => [...prev, ...pendingNewAttachments]);

        const notePayload = pendingNote !== initialNote ? { postInspectionNote: pendingNote } : {};

        const { data } = await uploadAttachmentsToPoi({
          variables: {
            input: {
              pointOfInterestId: selectedPoiInfo.id,
              ...notePayload,
              files: pendingNewAttachments.map(({ uuid, name, type, size }) => ({
                uuid: uuid || '',
                name,
                size: size || 0,
                mimeType: type,
                imageType: ImageType.PostInspection,
              })),
              lastModifiedFor: 'Media Upload',
            },
          },
          refetchQueries: [SelectedPointOfInterestDocument],
          onError: (error) => {
            showSnackBar(error.message, 'error');
            setIsUpdating(false);
          },
        });

        if (data?.uploadAttachmentsToPoi?.length) {
          setPoiAttachments((prev) =>
            prev.map((attachment) => {
              const uploaded = data.uploadAttachmentsToPoi?.find((u) => u.uuid === attachment.uuid);
              if (uploaded) {
                return {
                  ...attachment,
                  url: uploaded.url,
                  resourcePath: uploaded.resourcePath,
                  id: uploaded.id,
                  uploadStatus: FileUploadStatus.Success,
                };
              }
              return attachment;
            })
          );
        }
      } else {
        setIsUpdating(false);
      }

      showSnackBar('Changes saved successfully', 'success');
    } catch (error) {
      console.error('Error saving changes:', error);
      showSnackBar('Failed to save changes', 'error');
      setIsUpdating(false);
    } finally {
      setPendingNewAttachments([]);
      setIsNoteEditing(false);
    }
  }, [
    pendingNote,
    initialNote,
    pendingNewAttachments,
    selectedPoiInfo.id,
    updateNote,
    uploadAttachmentsToPoi,
    setIsUpdating,
    showSnackBar,
    setPoiAttachments,
  ]);

  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSave;
    }
  }, [saveRef, handleSave]);

  return (
    <MediaWrapperContainer sx={sx} className="mediaViewer">
      <MediaSectionTitle>Notes and attachments</MediaSectionTitle>

      <PostInspectionNotesEditor
        note={pendingNote ?? initialNote}
        onNoteChange={setPendingNote}
        isEditing={isNoteEditing}
        onEditingChange={(editing) => {
          setIsNoteEditing(editing);
        }}
        canEdit={isEditMode && userCanUploadMedia}
      />

      <input
        ref={fileInputRef}
        accept="image/jpeg,image/png,application/pdf,video/mp4"
        style={{ display: 'none' }}
        multiple
        type="file"
        onChange={handleFileSelect}
      />

      {userCanUploadMedia && (
        <UploadButton onClick={() => fileInputRef.current?.click()} role="button" tabIndex={0}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AbyssIcon iconName="load-view" style={{ width: '1.3rem', height: '1.2rem' }} />
            <UploadButtonText variant="button">Upload or drag file</UploadButtonText>
          </Stack>
        </UploadButton>
      )}

      <AttachmentsViewer
        attachments={postInspectionImages}
        allAttachments={postInspectionImages}
        setPoiAttachments={setPoiAttachments}
        selectedPoiInfo={selectedPoiInfo}
        setIsUpdating={setIsUpdating}
        isUpdating={isUpdating}
        location={LocationTag.ViewerSelectedPoiDetails}
        layout="list"
      />

      {pendingNewAttachments.length > 0 &&
        pendingNewAttachments.map((attachment, index) => (
          <FileDisplay
            key={attachment.uuid || `pending-${index}`}
            collectionName="poi"
            selectedAttachment={attachment}
            selectedAttachmentIndex={index}
            handleRemoveAttachment={handleRemovePendingAttachment}
            showRemoveIcon
            location={LocationTag.ViewerSelectedPoiDetails}
            layout="list"
          />
        ))}
    </MediaWrapperContainer>
  );
};
